import { useCallback, useEffect, useMemo, useRef } from "react";
import * as echarts from 'echarts';

const GraphViewer = ({ highlightNodeId, data, onNodeClick }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const nodes = useMemo(() => data?.nodes || [], [data]);
    const links = useMemo(() => data?.links || [], [data]);
    const categories = useMemo(() => data?.categories || [], [data]);

    const resolveNodeIndex = useCallback((nodeRef) => {
        if (typeof nodeRef === 'number') {
            return nodeRef;
        }
        return nodes.findIndex(item => item.name === nodeRef);
    }, [nodes]);

    const getStyledNodes = useCallback(() => {
        const matchIdx = resolveNodeIndex(highlightNodeId);
        const matchItem = nodes.find(item => item.name === highlightNodeId) || '';

        const connectedNames = new Set();
        links.forEach(link => {
            const sourceIdx = resolveNodeIndex(link.source);
            const targetIdx = resolveNodeIndex(link.target);

            if (sourceIdx === matchIdx && nodes[targetIdx]) {
                connectedNames.add(nodes[targetIdx].name);
            }
            if (targetIdx === matchIdx && nodes[sourceIdx]) {
                connectedNames.add(nodes[sourceIdx].name);
            }
        });

        const preservedNames = new Set([highlightNodeId, ...connectedNames]);

        return nodes.map(node => {
            const isHighlight = node.name === highlightNodeId;

            let itemStyle;
            if (highlightNodeId !== '' && highlightNodeId !== undefined) {
                itemStyle = preservedNames.has(node.name) ? undefined : { opacity: 0.1 };
            }

            if (isHighlight && matchItem) {
                return {
                    ...node,
                    itemStyle: {
                        ...itemStyle,
                        borderColor: categories[matchItem.category]?.itemStyle?.color,
                        borderWidth: 10,
                        shadowBlur: 50,
                        shadowColor: categories[matchItem.category]?.itemStyle?.color
                    },
                    symbolSize: node.symbolSize,
                }
            }
            return {
                ...node,
                itemStyle,
                symbolSize: node.symbolSize,
            };
        });
    }, [categories, highlightNodeId, links, nodes, resolveNodeIndex]);


    const getStyledLinks = useCallback(() => {
        const matchIdx = resolveNodeIndex(highlightNodeId);

        const highlightedLinkIndices = new Set();
        if (matchIdx !== -1) {
            links.forEach((link, idx) => {
                const sourceIdx = resolveNodeIndex(link.source);
                const targetIdx = resolveNodeIndex(link.target);
                if (sourceIdx === matchIdx || targetIdx === matchIdx) {
                    highlightedLinkIndices.add(idx);
                }
            });
        }

        const linkValues = links.map(link => link.value || 1);
        const minValue = linkValues.length > 0 ? Math.min(...linkValues) : 0;
        const maxValue = linkValues.length > 0 ? Math.max(...linkValues) : 0;
        const valueRange = maxValue - minValue;

        return links.map((link, idx) => {
            const isHighlighted = highlightedLinkIndices.has(idx);

            let lineWidth;
            if (isHighlighted) {
                if (valueRange === 0) {
                    lineWidth = 10;
                } else {
                    const normalizedValue = ((link.value || 1) - minValue) / valueRange;
                    lineWidth = 1 + normalizedValue * 19;
                }
            } else {
                lineWidth = 1;
            }

            return {
                ...link,
                lineStyle: {
                    color: isHighlighted ? '#000000' : '#d3d3d3',
                    width: lineWidth,
                    opacity: 0.6
                },
                label: {
                    show: isHighlighted,
                    formatter: isHighlighted ? `${link.value}` : '',
                    color: '#000',
                    fontWeight: 'bold',
                    fontSize: 10
                }
            };
        });
    }, [highlightNodeId, links, resolveNodeIndex]);

    useEffect(() => {
        if (chartRef.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }
        return () => {
            chartInstance.current?.dispose();
        };
    }, []);

    useEffect(() => {
        if (!chartInstance.current) return;

        const handleClick = (params) => {
            if (params?.dataType === 'node' && params?.data?.name) {
                onNodeClick && onNodeClick(params.data.name);
            }
        };

        chartInstance.current.on('click', handleClick);
        return () => {
            chartInstance.current?.off('click', handleClick);
        };
    }, [onNodeClick]);

    useEffect(() => {
        const handleResize = () => {
            chartInstance.current?.resize();
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        chartInstance.current?.setOption({
            legend: {
                data: categories.map(item => item.name)
            },
            series: [
                {
                    type: 'graph',
                    layout: 'force',
                    animation: false,
                    draggable: true,
                    data: getStyledNodes(),
                    categories,
                    force: {
                        repulsion: 150,
                        edgeLength: 100
                    },
                    edges: getStyledLinks(),
                    lineStyle: {
                        opacity: 0.7,
                        curveness: 0.2
                    },
                    label: {
                        color: '#000',
                    }
                }
            ]
        });
    }, [categories, getStyledLinks, getStyledNodes, highlightNodeId]);

    return <div ref={chartRef} style={{ width: '100%', height: '100%' }} />;
};

export default GraphViewer;
